import {
  useContext,
  useState,
  useEffect,
  useRef,
  type PropsWithChildren,
} from "react";
import {
  Tabs as TabsPrimitive,
  Collection,
  type Key,
} from "react-aria-components";
import {
  type HeadingLevel,
  type PropsWithOptionalTestID,
} from "@boi/components-core";

import * as S from "./Tabs.styled";
import type { TabsItem, TabListView } from "./Tabs.types";
import ButtonTabsContext, { TabsProvider } from "./TabsContext";
import { MobileTabList } from "./MobileTabList";
import { DesktopTabList } from "./DesktopTabList";

export type TabsProps = PropsWithOptionalTestID &
  PropsWithChildren<{
    items: TabsItem[];
    activeItemId: TabsItem["id"];
    mobileHeading: string;
    desktopHeading?: string;
    desktopHeadingLevel?: HeadingLevel;
    onChange: (activeItemId: TabsItem["id"]) => void;
    withPadding?: boolean;
  }>;

export function Tabs(props: TabsProps) {
  const { children, testID = "tabs", ...otherProps } = props;
  const { activeItemId, onChange } = otherProps;

  function handleSelectionChange(key: Key) {
    onChange(key as TabsItem["id"]);
  }

  return (
    <TabsProvider {...otherProps} testID={testID}>
      <TabsPrimitive
        selectedKey={activeItemId}
        onSelectionChange={handleSelectionChange}
        data-testid={testID}
      >
        {children}
      </TabsPrimitive>
    </TabsProvider>
  );
}

Tabs.Items = function TabsList() {
  const [tabListView, setTabListView] = useState<TabListView>(null);

  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!outerRef.current || !innerRef.current) {
      return;
    }

    const options = {
      threshold: 1,
      root: outerRef.current,
    };

    const observer = new IntersectionObserver(([entry]) => {
      if (entry) {
        setTabListView(entry.isIntersecting ? "desktop" : "mobile");
      }
    }, options);

    observer.observe(innerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={outerRef}>
      <S.DesktopTabListWrapper ref={innerRef} $tabListView={tabListView}>
        <DesktopTabList />
      </S.DesktopTabListWrapper>

      {tabListView === "mobile" && <MobileTabList />}
    </div>
  );
};

Tabs.Panels = function TabsPanels() {
  const { items, withPadding = true, testID } = useContext(ButtonTabsContext);

  return (
    <S.TabPanels data-testid={`${testID}-panels`}>
      <Collection items={items}>
        {(item) => (
          <S.TabPanel
            key={item.id}
            id={item.id}
            $withPadding={withPadding}
            $withIcon={Boolean(item.icon)}
            data-testid={`${testID}-panel-${item.id}`}
          >
            {item.panel}
          </S.TabPanel>
        )}
      </Collection>
    </S.TabPanels>
  );
};

export default Tabs;
